import {
  percent,
  ratio,
  toneOf,
  type DashboardLimit,
  type RtoSlice,
  type Tone,
} from "@estelle/domain";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  LOW_SAMPLE,
  courierLabel,
  percentLabel,
} from "@/modules/dashboard/format";
import { RTO_MIN_SHIPMENTS, groupRto } from "@/modules/dashboard/rto";

const FILL: Record<Tone, string> = {
  ok: "bg-primary",
  amber: "bg-warning",
  red: "bg-destructive",
};

const WORD: Record<Tone, string> = {
  ok: "",
  amber: "high",
  red: "over limit",
};

/** A bar's full width is this many percent; past it the bar is simply full. */
const FULL_SCALE = 40;

/**
 * RTO rate for each courier or state (#528), highest first.
 *
 * A bar is amber over the amber limit and red over the red one, and says so in
 * words beside the figure - colour is never the only signal. Only couriers and
 * states with at least `RTO_MIN_SHIPMENTS` finished shipments are rated on
 * their own (#638); the rest are added up into one "Other" line that says how
 * many it holds. Below `LOW_SAMPLE` a row is still drawn muted and marked, so
 * one RTO among a dozen parcels is not a headline.
 */
export function RtoBars({
  title,
  description,
  rows,
  limit,
  label = courierLabel,
  noun,
  empty,
}: {
  title: string;
  description: string;
  rows: readonly RtoSlice[];
  limit: DashboardLimit;
  label?: (key: string) => string;
  /** What a row is, for the "Other" line: `courier` or `state`. */
  noun: string;
  empty: string;
}) {
  const grouped = groupRto(rows);
  const ranked = grouped.rated
    .map((row) => ({
      ...row,
      rate: percent(ratio(row.rto, row.outcomes)) ?? 0,
    }))
    .sort((a, b) => b.rate - a.rate || b.outcomes - a.outcomes);
  const other = grouped.other;

  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">{empty}</p>
        ) : (
          <ul className="flex flex-col gap-2.5">
            {ranked.map((row) => {
              const low = row.outcomes < LOW_SAMPLE;
              const tone = low ? "ok" : toneOf(row.rate, limit);
              return (
                <Row
                  key={row.key}
                  name={label(row.key)}
                  rate={row.rate}
                  outcomes={row.outcomes}
                  fill={low ? "bg-muted-foreground/50" : FILL[tone]}
                  flag={
                    low ? "low sample" : tone !== "ok" ? WORD[tone] : undefined
                  }
                  muted={low}
                />
              );
            })}
            {other ? (
              <Row
                name="Other"
                rate={percent(ratio(other.rto, other.outcomes)) ?? 0}
                outcomes={other.outcomes}
                fill="bg-muted-foreground/50"
                muted
              />
            ) : null}
          </ul>
        )}
        {other ? (
          <p className="text-muted-foreground text-xs">
            Other adds up {String(other.members)}{" "}
            {other.members === 1 ? noun : `${noun}s`} with fewer than{" "}
            {String(RTO_MIN_SHIPMENTS)} finished shipments each (
            {String(other.outcomes)} in total), too few to rate alone.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Row({
  name,
  rate,
  outcomes,
  fill,
  flag,
  muted = false,
}: {
  name: string;
  rate: number;
  outcomes: number;
  fill: string;
  flag?: string;
  muted?: boolean;
}) {
  return (
    <li className="grid grid-cols-[minmax(0,8.5rem)_1fr_auto] items-center gap-x-3 text-sm">
      <span
        className={cn("truncate", muted && "text-muted-foreground")}
        title={name}
      >
        {name}
      </span>
      <span aria-hidden className="bg-muted relative h-2.5 rounded-full">
        <span
          className={cn("absolute inset-y-0 left-0 rounded-full", fill)}
          style={{
            width: `${String(Math.min(100, (rate / FULL_SCALE) * 100))}%`,
          }}
        />
      </span>
      <span className="text-right tabular-nums">
        <span className={cn("font-medium", muted && "text-muted-foreground")}>
          {percentLabel(rate)}
        </span>
        <span className="text-muted-foreground text-xs">
          {" "}
          · n = {String(outcomes)}
        </span>
        {flag ? (
          <span
            className={cn(
              "text-xs",
              flag === "low sample" ? "text-muted-foreground" : "font-semibold",
            )}
          >
            {" "}
            · {flag}
          </span>
        ) : null}
      </span>
    </li>
  );
}
